import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, Award, Save, Megaphone } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';

interface NotificationSettings {
  event_reminders: boolean;
  rsvp_confirmations: boolean;
  attendance_confirmations: boolean;
  achievement_updates: boolean;
  weekly_summary: boolean;
  announcements: boolean;
}

const NotificationPreferences: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({
    event_reminders: true,
    rsvp_confirmations: true,
    attendance_confirmations: true,
    achievement_updates: true,
    weekly_summary: false,
    announcements: true
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await axios.get('/members/notification-preferences');
      if (response.data.data) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/members/notification-preferences', settings);
      toast.success('Notification preferences saved successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0D5128]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      {/* Member Header */}
      <header className="bg-[#0D5128] shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/member/dashboard')}
            className="text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="bg-white/15 p-1.5 rounded-lg">
            <img src="/website-04.png" alt="Hammad Foundation" className="h-7 w-auto object-contain" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg leading-tight">Notification Preferences</h1>
            <p className="text-green-200 text-xs">Manage your email notifications</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Settings Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail size={18} className="text-[#0D5128]" />
            Email Notifications
          </h2>

          <div className="space-y-3">
            {([
              { key: 'event_reminders', icon: Calendar, label: 'Event Reminders', desc: 'Receive reminders 24h and 1h before events' },
              { key: 'rsvp_confirmations', icon: Calendar, label: 'RSVP Confirmations', desc: 'Email confirmation when you RSVP to an event' },
              { key: 'attendance_confirmations', icon: Award, label: 'Attendance Confirmations', desc: 'Confirmation with points earned after check-in' },
              { key: 'achievement_updates', icon: Award, label: 'Achievement Updates', desc: 'Notified when you reach a new level (Bronze, Silver, Gold...)' },
              { key: 'announcements', icon: Megaphone, label: 'Announcements', desc: 'Alerts when admin posts a new announcement' },
              { key: 'weekly_summary', icon: Mail, label: 'Weekly Summary', desc: 'Weekly email with upcoming events and activity' },
            ] as { key: keyof NotificationSettings; icon: React.ElementType; label: string; desc: string }[]).map(({ key, icon: Icon, label, desc }) => (
              <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-8 h-8 bg-[#0D5128]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon size={15} className="text-[#0D5128]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">{label}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={settings[key]}
                    onChange={() => handleToggle(key)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0D5128]"></div>
                </label>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 bg-[#0D5128] hover:bg-green-800 disabled:opacity-60 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Preferences
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-4 p-4 bg-[#C49A3C]/10 border border-[#C49A3C]/30 rounded-xl">
          <p className="text-sm text-[#a97f2f]">
            All emails will be sent to the address associated with your account. You can update preferences at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
